import * as React from "react";
import {
  createFileRoute,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import {
  Button,
  Card,
  Column,
  DropdownButton,
  Icon,
  Row,
  Text,
  Tooltip,
  useDesignTokens,
} from "@gradience/ui";
import PageChrome from "../../../components/page-chrome";
import BoxPlotBar from "../../../components/charts/box-plot-bar";
import styled from "styled-components";
import { useQuery } from "@tanstack/react-query";
import { getQueryOptions } from "../../../lib/api";
import { useSelectedTest } from "../../../domain/use-latest-test";
import { isDatePast } from "../../../lib/date-utils";

export const Route = createFileRoute("/$test-slug/results/")({
  component: RouteComponent,
  validateSearch: (search): { preview?: boolean } => {
    return search.preview === true ? { preview: true } : {};
  },
});

const DashedLine = styled.span`
  border: 1px dashed ${({ theme }) => theme.colors.border.subdued};
`;

const grades = [
  { label: "All", value: "" },
  { label: "3rd", value: "3" },
  { label: "4th", value: "4" },
  { label: "5th", value: "5" },
  { label: "6th", value: "6" },
  { label: "7th", value: "7" },
  { label: "8th", value: "8" },
  { label: "9th", value: "9" },
  { label: "10th", value: "10" },
  { label: "11th", value: "11" },
  { label: "12th", value: "12" },
] as const;

type Grade = (typeof grades)[number]["value"];

function RouteComponent() {
  const designTokens = useDesignTokens();

  const [selectedGrade, setSelectedGrade] = React.useState<Grade>("");
  const { preview } = useSearch({ from: "/$test-slug/results/" });
  const selectedTest = useSelectedTest();
  const showAwards = preview || isDatePast(selectedTest.data?.completionDate);

  const results = useQuery(
    getQueryOptions({
      endpoint: "/results",
      queryParams: {
        ...(selectedGrade === "" ? {} : { gradeLevel: selectedGrade }),
        ...(showAwards ? { preview: "true" } : {}),
      },
    }),
  );
  const maxScored = Math.max(
    ...Object.values(results.data?.studentsAndAwards ?? {}).map(
      (percentiles) => percentiles.scoredCount,
    ),
  );

  return (
    <PageChrome loading={results.isLoading}>
      <Column
        style={{
          gap: 40,
          flexGrow: 1,
        }}
      >
        <Row
          gap={16}
          style={{
            alignItems: "flex-start",
          }}
        >
          <Column gap={4} style={{ flex: 1 }}>
            <Text textStyle="32px - Bold">Results</Text>
          </Column>
          <DropdownButton
            style={{ zIndex: 2 }}
            text={`Grade: ${
              selectedGrade === ""
                ? "All"
                : grades.find((g) => g.value === selectedGrade)?.label!
            }`}
            options={[...grades]}
            onSelect={(value) => {
              setSelectedGrade(value);
            }}
          />
        </Row>
        <Card
          style={{
            padding: 24,
            flexDirection: "row",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <ResultCard
            title="Percentiles"
            subtitle={selectedGrade === "" ? "Percentiles across all groups." : `Percentiles for ${grades.find((g) => g.value === selectedGrade)?.label} grade.`}
            tooltip={
              <Column gap={4}>
                <Text textStyle="14px - Medium">
                  Percentiles are calculated based on the normalized scores of
                  other students in the same grade.
                </Text>
                <Text textStyle="14px - Medium">
                  The light dot is the median percentile, meaning that 50% of
                  your students scored above this percentile and 50% scored
                  below. Hover over the dot to see the exact value.
                </Text>
              </Column>
            }
          >
            <Column gap={24} style={{ position: "relative" }}>
              <Row
                style={{
                  top: 0,
                  left: 47,
                  bottom: 33,
                  right: 0,
                  position: "absolute",
                  justifyContent: "space-between",
                  alignItems: "stretch",
                }}
              >
                <DashedLine />
                <DashedLine />
                <DashedLine />
              </Row>
              {Object.entries(results.data?.percentiles ?? {}).length === 0 ? (
                <Row
                  style={{
                    minHeight: 100,
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Text
                    textStyle="14px - Medium"
                    style={{
                      color: designTokens.colors.text.disabled,
                      marginLeft: 47,
                      zIndex: 1,
                    }}
                  >
                    No data
                  </Text>
                </Row>
              ) : (
                Object.entries(results.data?.percentiles ?? {}).map(
                  ([year, percentiles]) => (
                    <Row gap={8} style={{ alignItems: "center" }}>
                      <Text textStyle="14px - Medium" style={{ width: 36 }}>
                        {year}
                      </Text>
                      <BoxPlotBar
                        domainMin={0}
                        domainMax={1}
                        whiskerMin={percentiles.min}
                        q1={percentiles.q1}
                        median={percentiles.median}
                        q3={percentiles.q3}
                        whiskerMax={percentiles.max}
                      />
                    </Row>
                  ),
                )
              )}
              <Row gap={8} style={{ alignItems: "center" }}>
                <span style={{ width: 36, height: 20 }} />
                <Row style={{ flex: 1, justifyContent: "space-between" }}>
                  <Text
                    textStyle="caption"
                    style={{
                      color: designTokens.colors.text.disabled,
                      transform: "translateX(50%)",
                    }}
                  >
                    0
                  </Text>
                  <Text
                    textStyle="caption"
                    style={{
                      color: designTokens.colors.text.disabled,
                      transform: "translateX(50%)",
                    }}
                  >
                    50
                  </Text>
                  <Text
                    textStyle="caption"
                    style={{
                      color: designTokens.colors.text.disabled,
                      transform: "translateX(50%)",
                    }}
                  >
                    100
                  </Text>
                </Row>
              </Row>
            </Column>
          </ResultCard>
          {selectedGrade === "" && <ResultCard
            title="Average Score by Grade"
            subtitle="Average raw score per grade, up to two years."
          >
            <GradeScoreChart
              avgScoreData={results.data?.avgScoreByGrade ?? {}}
              awardsData={results.data?.awardsByGrade ?? {}}
            />
          </ResultCard>}
          {showAwards && (
            <ResultCard
              title="Students & Awards"
              subtitle="Student and award count school-wide"
              tooltip={
                <Column gap={4}>
                  <Text textStyle="14px - Medium">
                    Each bar shows the award distribution for that year. Red is
                    no award, grey is Caesar (60th percentile and above), and
                    gold is Augustus (85th percentile and above). Bar width is
                    proportional to total students scored.
                  </Text>
                </Column>
              }
            >
              <Column gap={24} style={{ position: "relative" }}>
                <Row
                  style={{
                    top: 0,
                    left: 47,
                    bottom: 33,
                    right: 0,
                    position: "absolute",
                    justifyContent: "space-between",
                    alignItems: "stretch",
                  }}
                >
                  <DashedLine />
                  <DashedLine />
                  <DashedLine />
                </Row>
                {Object.entries(results.data?.studentsAndAwards ?? {})
                  .length === 0 ? (
                  <Row
                    style={{
                      minHeight: 100,
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Text
                      textStyle="14px - Medium"
                      style={{
                        color: designTokens.colors.text.disabled,
                        marginLeft: 47,
                        zIndex: 1,
                      }}
                    >
                      No data
                    </Text>
                  </Row>
                ) : (
                  Object.entries(results.data?.studentsAndAwards ?? {}).map(
                    ([year, percentiles]) => (
                      <Row gap={15} style={{ alignItems: "center" }}>
                        <Text textStyle="14px - Medium" style={{ width: 36 }}>
                          {year}
                        </Text>
                        <StudentAndAwardsBars
                          scoredCount={percentiles.scoredCount}
                          goldAwardsCount={percentiles.goldAwardsCount}
                          silverAwardsCount={percentiles.silverAwardsCount}
                          max={maxScored}
                        />
                      </Row>
                    ),
                  )
                )}
                <Row gap={8} style={{ alignItems: "center" }}>
                  <span style={{ width: 36, height: 20 }} />
                  <Row style={{ flex: 1, justifyContent: "space-between" }}>
                    <Text
                      textStyle="caption"
                      style={{
                        color: designTokens.colors.text.disabled,
                        transform: "translateX(50%)",
                      }}
                    >
                      0
                    </Text>
                    {maxScored === 1 ? null : (
                      <Text
                        textStyle="caption"
                        style={{
                          color: designTokens.colors.text.disabled,
                          transform: "translateX(50%)",
                        }}
                      >
                        {Math.floor(maxScored / 2)}
                      </Text>
                    )}
                    <Text
                      textStyle="caption"
                      style={{
                        color: designTokens.colors.text.disabled,
                        transform: "translateX(50%)",
                      }}
                    >
                      {maxScored}
                    </Text>
                  </Row>
                </Row>
              </Column>
            </ResultCard>
          )}
        </Card>
      </Column>
    </PageChrome>
  );
}

function StudentAndAwardsBars({
  scoredCount,
  goldAwardsCount,
  silverAwardsCount,
  max,
}: {
  scoredCount: number;
  goldAwardsCount: number;
  silverAwardsCount: number;
  max: number;
}) {
  if (scoredCount === 0) return null;

  const noAwardCount = Math.max(
    0,
    scoredCount - goldAwardsCount - silverAwardsCount,
  );

  return (
    <Tooltip
      style={{ display: "block", width: "100%" }}
      content={
        <Column gap={2}>
          <Text textStyle="12px - Bold" style={{ color: "#C2600A" }}>
            Augustus: {goldAwardsCount}
          </Text>
          <Text textStyle="12px - Bold" style={{ color: "#4B5563" }}>
            Caesar: {silverAwardsCount}
          </Text>
          <Text textStyle="12px - Bold" style={{ color: NO_AWARD_COLOR }}>
            No award: {noAwardCount}
          </Text>
          <Text textStyle="12px - Bold">Total: {scoredCount}</Text>
        </Column>
      }
    >
      <div
        style={{
          width: `${(scoredCount / max) * 100}%`,
          height: 14,
          minWidth: 24,
          display: "flex",
          borderRadius: 8,
          overflow: "hidden",
        }}
      >
        <div style={{ flex: noAwardCount, backgroundColor: NO_AWARD_COLOR }} />
        <div
          style={{ flex: silverAwardsCount, backgroundColor: SILVER_COLOR }}
        />
        <div style={{ flex: goldAwardsCount, backgroundColor: GOLD_COLOR }} />
      </div>
    </Tooltip>
  );
}

function ResultCard({
  children,
  title,
  subtitle,
  tooltip,
}: {
  children?: React.ReactNode;
  title: string;
  subtitle: string;
  tooltip?: React.ReactNode;
}) {
  const designTokens = useDesignTokens();

  return (
    <Column
      style={{
        padding: 24,
        border: `1px solid ${designTokens.colors.border.light}`,
        borderRadius: 16,
        flexBasis: 400,
        flexShrink: 0,
        flexGrow: 1,
        gap: 24,
      }}
    >
      <Row gap={8}>
        <Column gap={4} style={{ flex: 1 }}>
          <Text textStyle="20px - Bold">{title}</Text>
          <Text textStyle="14px - Medium">{subtitle}</Text>
        </Column>
        {tooltip !== undefined ? (
          <Row style={{ padding: 4, alignItems: "center" }}>
            <Tooltip content={tooltip}>
              <Icon name="information-circle-contained" />
            </Tooltip>
          </Row>
        ) : null}
      </Row>
      {children}
    </Column>
  );
}

const GRADE_LEVEL_ORDER = [
  "FIRST",
  "SECOND",
  "THIRD",
  "FOURTH",
  "FIFTH",
  "SIXTH",
  "SEVENTH",
  "EIGHTH",
  "NINTH",
  "TENTH",
  "ELEVENTH",
  "TWELFTH",
] as const;

const GRADE_LEVEL_LABEL: Record<string, string> = {
  FIRST: "1st",
  SECOND: "2nd",
  THIRD: "3rd",
  FOURTH: "4th",
  FIFTH: "5th",
  SIXTH: "6th",
  SEVENTH: "7th",
  EIGHTH: "8th",
  NINTH: "9th",
  TENTH: "10th",
  ELEVENTH: "11th",
  TWELFTH: "12th",
};

const MAX_SCORE = 40;
const Y_LINES = [0, 10, 20, 30, 40];
const CHART_HEIGHT = 120;
const MAX_BAR_WIDTH = 32;
const BAR_GAP = 3;
const GROUP_GAP = 16;
const Y_AXIS_WIDTH = 24;

const GOLD_COLOR = "#FEC035";
const SILVER_COLOR = "#9CA3AF";
const NO_AWARD_COLOR = "#DC2626";
const PAST_YEAR_BORDER = "#6B7280";

function GradeScoreChart({
  avgScoreData,
  awardsData,
}: {
  avgScoreData: Record<string, Record<string, number>>;
  awardsData: Record<
    string,
    Record<
      string,
      { goldCount: number; silverCount: number; noAwardCount: number }
    >
  >;
}) {
  const designTokens = useDesignTokens();

  const allYears = Object.keys(avgScoreData).sort().slice(-2);
  const currentYear = allYears[allYears.length - 1];
  const pastYears = allYears.slice(0, -1);

  const activeGrades = GRADE_LEVEL_ORDER.filter((gl) =>
    allYears.some((y) => avgScoreData[y]?.[gl] !== undefined),
  );

  if (activeGrades.length === 0) {
    return (
      <Row
        style={{
          minHeight: 100,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Text
          textStyle="14px - Medium"
          style={{ color: designTokens.colors.text.disabled }}
        >
          No data
        </Text>
      </Row>
    );
  }

  return (
    <Column gap={12}>
      {/* Legend */}
      <Row gap={12} style={{ flexWrap: "wrap", alignItems: "center" }}>
        <Text
          textStyle="12px - Bold"
          style={{ color: designTokens.colors.text.subdued }}
        >
          {currentYear}:
        </Text>
        {[
          {
            color: GOLD_COLOR,
            label: "Augustus (85th+)",
            textColor: "#C2600A",
          },
          {
            color: SILVER_COLOR,
            label: "Caesar (60th+)",
            textColor: "#4B5563",
          },
          { color: NO_AWARD_COLOR, label: "No award" },
        ].map(
          ({
            color,
            label,
            textColor,
          }: {
            color: string;
            label: string;
            textColor?: string;
          }) => (
            <Row key={label} gap={4} style={{ alignItems: "center" }}>
              <div
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 2,
                  backgroundColor: color,
                }}
              />
              <Text
                textStyle="caption"
                style={{ color: textColor ?? designTokens.colors.text.subdued }}
              >
                {label}
              </Text>
            </Row>
          ),
        )}
        {pastYears.map((year) => (
          <Row key={year} gap={4} style={{ alignItems: "center" }}>
            <div
              style={{
                width: 10,
                height: 10,
                borderRadius: 2,
                border: `1.5px dashed ${PAST_YEAR_BORDER}`,
                backgroundColor: "white",
              }}
            />
            <Text
              textStyle="12px - Bold"
              style={{ color: designTokens.colors.text.subdued }}
            >
              {year}
            </Text>
          </Row>
        ))}
      </Row>

      <Row gap={8} style={{ alignItems: "flex-start" }}>
        {/* Y-axis labels centered on each grid line */}
        <div
          style={{
            position: "relative",
            width: Y_AXIS_WIDTH,
            height: CHART_HEIGHT,
            flexShrink: 0,
          }}
        >
          {Y_LINES.map((v) => (
            <div
              key={v}
              style={{
                position: "absolute",
                right: 4,
                bottom: (v / MAX_SCORE) * CHART_HEIGHT,
                transform: "translateY(50%)",
                lineHeight: 1,
              }}
            >
              <Text
                textStyle="caption"
                style={{ color: designTokens.colors.text.disabled }}
              >
                {v}
              </Text>
            </div>
          ))}
        </div>

        {/* Chart body */}
        <Column gap={6} style={{ flex: 1 }}>
          <div style={{ position: "relative", height: CHART_HEIGHT }}>
            {/* Grid lines */}
            {Y_LINES.map((v) => (
              <div
                key={v}
                style={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  bottom: (v / MAX_SCORE) * CHART_HEIGHT,
                  height: 1,
                  backgroundColor:
                    v === 0
                      ? designTokens.colors.border.subdued
                      : designTokens.colors.border.light,
                }}
              />
            ))}

            {/* Bars pinned to bottom */}
            <Row
              style={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                alignItems: "flex-end",
                gap: GROUP_GAP,
              }}
            >
              {activeGrades.map((gradeLevel) => (
                <Row
                  key={gradeLevel}
                  style={{
                    flex: 1,
                    alignItems: "flex-end",
                    justifyContent: "center",
                    gap: BAR_GAP,
                  }}
                >
                  {allYears.map((year) => {
                    const score = avgScoreData[year]?.[gradeLevel];
                    const barHeight =
                      score !== undefined
                        ? Math.max((score / MAX_SCORE) * CHART_HEIGHT, 2)
                        : 0;
                    const isCurrent = year === currentYear;

                    if (!isCurrent) {
                      return (
                        <Tooltip
                          key={year}
                          style={{ flex: 1, maxWidth: MAX_BAR_WIDTH }}
                          content={
                            score !== undefined ? (
                              <Text textStyle="14px - Medium">
                                {score.toFixed(1)}
                              </Text>
                            ) : undefined
                          }
                        >
                          <div
                            style={{
                              width: "100%",
                              height: barHeight,
                              border:
                                score !== undefined
                                  ? `1.5px dashed ${PAST_YEAR_BORDER}`
                                  : undefined,
                              backgroundColor: "white",
                              borderRadius: "3px 3px 0 0",
                              boxSizing: "border-box",
                            }}
                          />
                        </Tooltip>
                      );
                    }

                    const awards = awardsData[year]?.[gradeLevel];
                    const total = awards
                      ? awards.goldCount +
                        awards.silverCount +
                        awards.noAwardCount
                      : 0;
                    const goldH =
                      awards && total > 0
                        ? (awards.goldCount / total) * barHeight
                        : 0;
                    const silverH =
                      awards && total > 0
                        ? (awards.silverCount / total) * barHeight
                        : 0;

                    const tooltipContent =
                      score !== undefined ? (
                        <Column gap={2}>
                          <Text textStyle="14px - Medium">
                            {score.toFixed(1)}
                          </Text>
                          {awards && total > 0 && (
                            <>
                              <Text
                                textStyle="12px - Bold"
                                style={{ color: "#C2600A" }}
                              >
                                Augustus: {awards.goldCount}
                              </Text>
                              <Text
                                textStyle="12px - Bold"
                                style={{ color: "#4B5563" }}
                              >
                                Caesar: {awards.silverCount}
                              </Text>
                              <Text
                                textStyle="12px - Bold"
                                style={{ color: NO_AWARD_COLOR }}
                              >
                                None: {awards.noAwardCount}
                              </Text>
                            </>
                          )}
                        </Column>
                      ) : undefined;

                    return (
                      <Tooltip
                        key={year}
                        style={{ flex: 1, maxWidth: MAX_BAR_WIDTH }}
                        content={tooltipContent}
                      >
                        <div
                          style={{
                            width: "100%",
                            height: barHeight,
                            borderRadius: "3px 3px 0 0",
                            overflow: "hidden",
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <div
                            style={{
                              height: goldH,
                              backgroundColor: GOLD_COLOR,
                              flexShrink: 0,
                            }}
                          />
                          <div
                            style={{
                              height: silverH,
                              backgroundColor: SILVER_COLOR,
                              flexShrink: 0,
                            }}
                          />
                          <div
                            style={{ flex: 1, backgroundColor: NO_AWARD_COLOR }}
                          />
                        </div>
                      </Tooltip>
                    );
                  })}
                </Row>
              ))}
            </Row>
          </div>

          {/* X-axis grade labels */}
          <Row style={{ gap: GROUP_GAP }}>
            {activeGrades.map((gradeLevel) => (
              <Text
                key={gradeLevel}
                textStyle="caption"
                style={{
                  flex: 1,
                  color: designTokens.colors.text.disabled,
                  textAlign: "center",
                  whiteSpace: "nowrap",
                }}
              >
                {GRADE_LEVEL_LABEL[gradeLevel]}
              </Text>
            ))}
          </Row>
        </Column>
      </Row>
    </Column>
  );
}
